/**
 * web/src/App.tsx —— 应用装配（**装配面 · main**）：**会话门** → 应用壳。
 *
 * 结构：`transport`（WS 下行帧）→ `shell/AppShell`（壳结构＝`shell/shell.decl.yaml`；
 *      界面清单＝`views/*.yaml` 派生 → 各界面渲染）。
 * 会话：未登录 ⇒ `shell/LoginView`（凭据交入口侧；前端零凭据、不存 token）；
 *       已登录 ⇒ `AppShell`（顶栏＝壳栏、不留身份位；主体＝各界面渲染（见上结构段）；登出＝设置面板唯一登出口）。
 *       依据 `web/CONTRACT.md` §2／§6。
 * 纪律：**前端零取数**；一切网络只经 `./transport`（唯一实装通道＝WS）；设置面持久化**只落 UI 偏好**。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "./shell/AppShell";
import LoginView from "./shell/LoginView";
import { sessionsFromViews, useProjection } from "./shell/useProjection";
import { createModel, deleteModel, listModels, toProviderGroups, updateModel } from "./transport/models";
import { formatSessionTime } from "./shell/sessionTime";
import { WsClient } from "./transport";
import { currentActor, type FetchLike } from "./transport/session";
import { createChatSession, deleteChatSession, listChatSessions, type ChatSession } from "./transport/chatSessions";
import { sendStop } from "./transport/command";
import type { DecisionOutcome } from "./cards/decision";
// ★F3d：附件**文件名**读面（读面按设计只带引用；名字由既有 `/api/files` 取回，前端建 id→名映射）
import { fileNames, listFiles, type FileItem } from "./transport/files";
import { buildArtifactList } from "./components/rightbar";
import type { ApprovalCard } from "./transport/dto";
import type { SessionItem } from "./shell/types";
import type { AttachmentFileLike } from "./assembly/input/attachments";
import type { ClientHandlers } from "./views/chatStep";
import { E_CODES, humanizeError } from "./transport/errorText";

export interface AppProps {
  /** 出网注入（测试/宿主可换） */
  fetchFn?: FetchLike;
  /**
   * 宿主注入会话源（**优先**；注入 ⇒ 不读投影读面，也不被投影帧覆盖）。
   * 缺省 ⇒ 投影读面 `views/sessions.yaml` 装配（见 `useProjection.sessionsFromViews`），
   * 再缺省 ⇒ 入口侧会话面 `/api/sessions` 回执。**三源皆缺 ⇒ 空态＋原因**（禁假造条目）。
   */
  sessions?: SessionItem[];
  /** 宿主注入当前会话 id（缺省 ⇒ 入口侧回执/首个会话） */
  activeSessionId?: string;
  /** 宿主绑定「新建会话」通道（缺省 ⇒ 走入口侧 `POST /api/sessions`；两者皆缺 ⇒ 左栏显式禁用＋原因） */
  createSession?: () => void;
  /**
   * 上行受理面就绪声明（CONTRACT §3 v1.5：第三帧型 `command_ack` 已入契约 ⇒ 回执可收）。
   * `true` ⇒ 立即绑定发送；缺省 ⇒ 按 **WS 连接可用**（`status === "open"`）自动绑定；
   * 不可用 ⇒ 发送键**显式禁用＋可见原因**（禁「假发」）。
   */
  commandIngressReady?: boolean;
}

type Gate = "checking" | "anonymous" | "authenticated";

export default function App({ fetchFn, sessions: hostSessions, activeSessionId: hostActive,
                              createSession: hostCreate, commandIngressReady }: AppProps) {
  const [gate, setGate] = useState<Gate>("checking");
  const [actor, setActor] = useState<string>("");
  // ★20261005 会话面（大王令「新建会话失败」）：列表＝服务端真源；新建＝入口侧 REST（非 WS 命令）
  const [restSessions, setRestSessions] = useState<SessionItem[]>([]);
  const [localActiveSessionId, setActiveSessionId] = useState<string | undefined>(undefined);
  /**
   * ★C-Ⅷ 会话容器 → 读面取数：① 取值器供 **建连时上报**（`?session=`；W 客户端在 connect 时取）；
   * ② `sessionKey` 供 **切会话即重连**（读面按 `$active_session` 绑定的行只对当前会话有效）。
   */
  const sessionRef = useRef<string | undefined>(undefined);
  const [sessionKey, setSessionKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    currentActor(fetchFn)
      .then((res) => { if (alive) { setActor(res.actor); setGate("authenticated"); } })
      .catch(() => { if (alive) setGate("anonymous"); });
    return () => { alive = false; };
  }, [fetchFn]);

  /** 服务端回执 → 壳行（字段名对齐既有 `SessionItem`：`activity_state`→`activity`；null→undefined，不在前端造值） */
  const toItem = (row: ChatSession): SessionItem => ({
    id: row.id, title: row.title, state: row.state ?? undefined, activity: row.activity_state ?? undefined,
    time: formatSessionTime(row.created_at) || undefined,   // ★20261005：行内第二行＝时间（缺 ⇒ 不渲）
  });

  // 登录后拉会话列表（失败 ⇒ 留痕空列表，**不编造**）；新建会话＝写入口侧后用回执回填并选中
  useEffect(() => {
    if (gate !== "authenticated") return undefined;
    let alive = true;
    listChatSessions(fetchFn)
      .then((rows) => { if (alive) setRestSessions(rows.map(toItem)); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [gate, fetchFn]);

  const createSession = useCallback(async () => {
    const row = await createChatSession(fetchFn);
    setRestSessions((prev) => [toItem(row), ...prev.filter((item) => item.id !== row.id)]);
    setActiveSessionId(row.id);
  }, [fetchFn]);

  const makeClient = useCallback(
    (handlers: ClientHandlers): WsClient =>
      new WsClient({
        origin: { protocol: location.protocol, host: location.host },
        onFrame: handlers.onFrame,
        onStatus: handlers.onStatus,
        onError: handlers.onError,
        session: () => sessionRef.current ?? null,      // ★C-Ⅷ：建连时上报会话容器
      }),
    [],
  );
  /** ★F3d：`附件 file_id → 文件名` 映射 ＋ 同一读面的列表页（右栏产物面**同源**数据） */
  const [attachmentNames, setAttachmentNames] = useState<Record<string, string>>({});
  const [filePage, setFilePage] = useState<{ items: FileItem[]; total: number }>({ items: [], total: 0 });
  const attachmentNamesRef = useRef<Record<string, string>>({});
  attachmentNamesRef.current = attachmentNames;

  const { state, running, inputFor, send, sendAttach, switchSession, clientRef } =
    useProjection(makeClient, gate === "authenticated", sessionKey, attachmentNamesRef);


  /** 投影读面装配 → 会话行（按声明 `sidebar.session_row_source` 逐键命中；无源即空，不补造） */
  const projectedSessions = useMemo(() => sessionsFromViews(state.views), [state.views]);
  /**
   * 会话源**三档优先**（取到手即用，皆缺 ⇒ 空数组 ⇒ 左栏空态＋原因）：
   *   ① 宿主注入（`props.sessions`；注入即权威，不被投影帧覆盖）
   *   ② 投影读面 `views/sessions.yaml`（有装配才取）
   *   ③ 入口侧会话面 `/api/sessions` 回执
   */
  /** ★20261013 main 修（报障「会话删除静默失败」）：**视图层删除遮罩**——会话行实际来源优先
   * 投影源（`projectedSessions`）或宿主注入源，而删除处理器只动 `restSessions`（其源主归位所限）；
   * 源里未移除 ⇒ 行不退场（后端已 200）＝现象「静默失败」。修法＝视图层过滤已删 id（**不动源主**
   * 的数据，仅收窄呈现），源主各自的清单仍归其源主。 */
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(new Set());
  const sessions = (hostSessions ?? (projectedSessions.length > 0 ? projectedSessions : restSessions))
    .filter((item) => !deletedIds.has(item.id));
  const activeSessionId = hostActive ?? localActiveSessionId ?? (hostSessions ? undefined : sessions[0]?.id);
  /**
   * ★F3d：附件名映射的**取数**（会话指针可见后才接上）。
   * 既有读面 `GET /api/files?session_id=…`（只读、非第二通道）；取不到 ⇒ 诚实空映射（不造假名）。
   * 会话切换与每次上传成功后各刷一次。
   */
  const refreshAttachmentNames = useCallback(async (): Promise<void> => {
    if (!activeSessionId) {
      setAttachmentNames({});
      setFilePage({ items: [], total: 0 });
      return;
    }
    try {
      const page = await listFiles(fetchFn, { sessionId: activeSessionId });
      setAttachmentNames(fileNames(page));
      setFilePage({ items: page.items, total: Number(page.total ?? page.items.length) });
    } catch {
      setAttachmentNames({});
      setFilePage({ items: [], total: 0 });   // 读面不可达 ⇒ 诚实空集（右栏走 empty 态，不造假行）
    }
  }, [activeSessionId, fetchFn]);
  useEffect(() => {
    void refreshAttachmentNames();
  }, [refreshAttachmentNames]);

  /** ★F4 收尾：右栏**产物面**模型（与附件名映射**同一读面**同源；缺会话／读数缺席 ⇒ 诚实空集） */
  const filesModel = useMemo(
    () => buildArtifactList({ sessionId: activeSessionId ?? "", items: filePage.items,
                              total: filePage.total }),
    [activeSessionId, filePage],
  );


  /**
   * 附件上行注入面（★F2）：`uploadApi` 走**同一 WS 信封**（`transport/attach`）——零第二通道；
   * 未连／对象读不出内容／后端显式拒 ⇒ **显式失败**（回 `ok:false`，由附件栏呈现，绝不静默假成功）。
   * 会话归属由后端按**连接级上下文**判（前端不自报文件名/类型以外的任何东西）。
   */
  const uploadDeps = useMemo(
    () => ({
      uploadApi: async (file: AttachmentFileLike) => {
        const f = file as unknown as {
          name: string; type?: string; arrayBuffer?: () => Promise<ArrayBuffer>;
        };
        if (typeof f.arrayBuffer !== "function") {
          return { ok: false as const, errorText: "该附件内容无法读取，请更换文件后重试" };
        }
        try {
          const bytes = new Uint8Array(await f.arrayBuffer());
          const rec = await sendAttach(f.name, f.type || "application/octet-stream", bytes);
          void refreshAttachmentNames();     // ★F3d：新件入库 ⇒ 刷名字映射（条目随即显示文件名）
          return { ok: true as const, data: { fileId: rec.file_id, channel: "ws-uplink" } };
        } catch (e) {
          return { ok: false as const, errorText: String((e as Error)?.message ?? e) };
        }
      },
    }),
    [sendAttach, refreshAttachmentNames],
  );

  /**
   * ★F2 附件链前置：**建会话只经注入面**（`useComposerUpload` 的 A 方案，承大王令 20260920）——
   * 提交/上传时刻无会话 ⇒ 先建一个再继续（用户不必先手动点"＋"）；建不出 ⇒ 回空串 ⇒ 附件栏
   * 维持 `blocked:'no-session'`（**零隐式建会话**、不静默）。
   */
  const ensureSession = useCallback(async (): Promise<string> => {
    if (activeSessionId) return activeSessionId;
    const row = await createChatSession(fetchFn);
    setRestSessions((prev) => [toItem(row), ...prev.filter((item) => item.id !== row.id)]);
    setActiveSessionId(row.id);
    return row.id;
  }, [activeSessionId, fetchFn]);

  // ★C-Ⅷ：会话容器变化 ⇒ 重连（新快照按该会话取行）；取值器与 key 同源，避免两处口径
  useEffect(() => {
    sessionRef.current = activeSessionId;
    setSessionKey(activeSessionId);
  }, [activeSessionId]);
  /** 新建会话通道：宿主绑定优先 ⇒ 否则入口侧 REST（App 常驻可用，故左栏不禁用） */
  const onCreateSession = hostCreate ?? (() => { void createSession(); });
  /**
   * ★SESS2（L-PF-SESS-1 接线 ＋ L-PF-SESS-3 上行 · 方案 §四 B4+B5）——
   * **点存量会话 ⇒ 有动作**。原 `App` **未传** `onPickSession`（`Sidebar.tsx:90`
   * 的 `props.onPickSession?.(…)` 恒为 `undefined`）⇒ 点击**零动作、零上行帧**（真机确证）。
   *
   * 两件事，各归各位：
   *  ① **本地会话态随动**（读面口径）：`activeSessionId` 随动 ⇒ 左栏 `aria-current` 换行、
   *     中栏经 `activeSessionId → sessionKey → 建连上报 ?session=` **重连取该会话的行**；
   *  ② **上行 `session.switch`**（写面口径）：经唯一 WS 通道发
   *     `{kind:"command",action:"session.switch",target:<id>}`（后端 `SESSION_ACTIONS` 成员）。
   *
   * 未连（`switchSession` 返 `false`）⇒ **本地随动照做**、**不伪造切换成功**（不补造列表/中栏内容）。
   */
  const pickSession = useCallback((id: string) => {
    setActiveSessionId(id);
    switchSession(id);
  }, [switchSession]);

  /** ★L-PL-5b/5c（PL5）：动作面显式反馈位（删除失败／停止发不出 ⇒ 可见 toast；禁静默吞） */
  const [actionNotice, setActionNotice] = useState("");

  /**
   * ★L-PL-5c（PL5 前端三缺口）——**删除会话**（接线到调用点：Sidebar 行内删除入口 → AppShell
   * `onDeleteSession` → 本处理器 → `transport/chatSessions.deleteChatSession`）。
   * 既有后端契约 `DELETE /api/sessions/{id}`（软删；零新端点）；成功 ⇒ 行退场（只动本 App 自有
   * 源 `restSessions`——宿主注入/投影源的清单归其源主）；失败 ⇒ **显式 toast**（行留列表可重试，禁假成功）。
   */
  const deleteSession = useCallback(async (id: string) => {
    try {
      await deleteChatSession(id, fetchFn);
      setRestSessions((prev) => prev.filter((item) => item.id !== id));
      setDeletedIds((prev) => new Set(prev).add(id));   // ★视图层遮罩（行源可能是投影/宿主源）
      setActionNotice("");
      if (localActiveSessionId === id) setActiveSessionId(undefined);  // 删的＝当前会话 ⇒ 让位首个剩余会话
    } catch (err) {
      setActionNotice(err instanceof Error ? err.message : "删除会话失败（未知原因）");
    }
  }, [fetchFn, localActiveSessionId]);

  /**
   * ★L-PL-5b（PL5 前端三缺口）——**停止生成**上行（`kind:"stop"`＝后端 `KIND_CLOSED_SET` 闭集成员，
   * `platform.ws.command_accepted` 平台事实出口）。原链：Composer 停止钮 → AppShell `onStop` 透传面
   * （20261004 已在位）→ **宿主从未绑定** ⇒ `props.onStop?.()` 静默吞（点了没反应）。本条补宿主端。
   * 发不出（未连）⇒ 显式 toast（不假发）。
   */
  const stopRun = useCallback(() => {
    const client = clientRef.current;
    const ok = !!client && sendStop(client, activeSessionId);
    if (!ok) setActionNotice("停止指令未发出（连接未就绪）");
  }, [clientRef, activeSessionId]);

  /** ★L-PL-5b：裁决**已落账**的卡（`DecisionOutcome` 只在写面回执后产生）⇒ 退场＋待办计数随减 */
  const [decidedKeys, setDecidedKeys] = useState<ReadonlySet<string>>(new Set());
  /** 裁决落账结果回传（AppShell `onWaitDecided` 透传面 20261005 已在位；宿主端原缺 ⇒ 静默吞） */
  const onWaitDecided = useCallback((outcome: DecisionOutcome) => {
    setDecidedKeys((prev) => new Set(prev).add(String(outcome.record.key)));
  }, []);

  // ── 模型面（★20261005 报障①「添加模型无法保存」订正：后端四动词早已在位（F4 已接 seal），
  //    前端**从未接线** ⇒ 点保存＝零请求、静默无反应。本条＝补那条缺的线。）──
  const [modelGroups, setModelGroups] = useState<Array<{ provider: string; models: Array<{ id: string; label: string }> }>>([]);
  const [modelBusy, setModelBusy] = useState(false);
  const [modelError, setModelError] = useState("");

  const reloadModels = useCallback(async () => {
    try {
      setModelGroups(toProviderGroups(await listModels(fetchFn)));
      setModelError("");
    } catch (err) {
      setModelGroups([]);                                  // 缺席呈现（不补造行）
      setModelError(err instanceof Error ? humanizeError(err.message) : "模型列表取回失败");
    }
  }, [fetchFn]);

  const withModelBusy = useCallback(async (task: () => Promise<void>) => {
    setModelBusy(true);
    setModelError("");
    try {
      await task();
    } catch (err) {
      setModelError(err instanceof Error ? humanizeError(err.message) : "模型保存失败");
      throw err;                                           // 上抛 ⇒ 面板保留已填内容（成功才收表单）
    } finally {
      setModelBusy(false);
    }
  }, []);

  useEffect(() => {
    if (gate !== "authenticated") return;
    void reloadModels();
  }, [gate, reloadModels]);

  /** §7 连接组「立即重连」：只走既有 WS 客户端（不另开通道） */
  const reconnect = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;
    client.stop();
    client.start();
  }, [clientRef]);

  /** §7 高级「诊断导出」：**本地**汇总（历史缺片／失败证据）；不打网络、不上传 */
  const exportDiagnostics = useCallback(() => {
    const payload = {
      status: state.status,
      error: state.error,
      views: state.views.length,
      frames: state.payloads.length,
    };
    try {
      if (typeof document === "undefined" || typeof URL?.createObjectURL !== "function") return;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "xg-diagnostics.json";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      // 导出失败不吞：错误留给宿主/控制台（不假装成功）
      throw new Error(E_CODES.DIAGNOSTICS_EXPORT + "：诊断导出失败");
    }
  }, [state]);

  /** 待办（审批/提问）＝**焦点呈现**（R3）：只计审批载荷，不常驻侧栏；
   *  ★L-PL-5b：已裁决（已落账）的卡退场（`decidedKeys`）——不再计入待办、不再重呈 */
  const waitCards = useMemo(
    () => state.payloads
      .filter((item) => item.surface === "approval")
      .filter((item) => !decidedKeys.has(String((item as { key?: unknown }).key ?? ""))) as unknown as ApprovalCard[],
    [state.payloads, decidedKeys],
  );
  const pending = waitCards.length;

  if (gate === "checking") {
    return <div data-testid="app-checking">检查登录状态…</div>;
  }
  if (gate === "anonymous") {
    return (
      <LoginView
        fetchFn={fetchFn}
        onLoggedIn={(who) => { setActor(who); setGate("authenticated"); }}
      />
    );
  }
  return (
    <AppShell
      sessions={sessions}
      activeSessionId={activeSessionId}
      onCreateSession={onCreateSession}
      /* ★SESS2（B4）：点存量会话的接线点——此前**未传** ⇒ 左栏点击静默（真机 L-PF-SESS-1） */
      onPickSession={pickSession}
      /* ★L-PL-5c：删除会话接线（行内删除入口 → deleteSession → DELETE /api/sessions/{id}） */
      onDeleteSession={deleteSession}
      inputFor={inputFor}
      /* B10：**连接可用（WS open）且宿主绑定** ⇒ 发送键可用；`commandIngressReady` 可显式提前就绪。
         不可用 ⇒ 壳内**显式禁用＋可见原因**（声明 `main.composer.send.absent_note`）。 */
      onCommand={commandIngressReady === true || state.status === "open" ? send : undefined}
      /* ★AUF3（L-B1-RUN）：运行中信号（＝`useProjection` 由后端 run 态帧派生的 `state.running`）
         ⇒ 壳透传 `Composer`：繁忙期投递＝**插话（`kind:"steer"`）／排队**（`useComposerQueue`）。 */
      running={running}
      /* ★L-PL-5b：停止生成接线（原宿主未绑 ⇒ `props.onStop?.()` 静默吞） */
      onStop={stopRun}
      /* ★L-PL-5b：裁决落账回传接线（原宿主未绑 ⇒ 静默吞；现 ⇒ 已裁决卡退场＋计数随减） */
      onWaitDecided={onWaitDecided}
      uploadDeps={uploadDeps}
      ensureSession={ensureSession}
      filesModel={filesModel}
      outbox={state.outbox}
      sessionEntries={state.payloads}
      fetchFn={fetchFn}
      waitCards={waitCards}
      state={state.status}
      onReconnect={reconnect}
      onExportDiagnostics={exportDiagnostics}
      pendingActions={pending}
      modelGroups={modelGroups}
      modelBusy={modelBusy}
      modelError={modelError}
      onAddModel={async (values) => { await withModelBusy(async () => { await createModel(values, fetchFn); await reloadModels(); }); }}
      onUpdateModel={async (values) => { await withModelBusy(async () => { await updateModel(values.id, values, fetchFn); await reloadModels(); }); }}
      onDeleteModel={async (id) => { await withModelBusy(async () => { await deleteModel(id, fetchFn); await reloadModels(); }); }}
      /* ★L-PL-5b/5c：动作失败显式反馈（删除失败／停止发不出 ⇒ 可见 toast；禁静默吞） */
      overlayEntries={[
        ...(actionNotice ? [
          <span className="xg-toast xg-shell-overlay__toast--warning" data-testid="action-toast" key="action-toast">{actionNotice}</span>,
        ] : []),
        ...(state.error ? [
          <span className="xg-toast" data-testid="transport-toast" key="transport-toast">{state.error}</span>,
        ] : []),
      ]}
    />
  );
}
